import React from 'react';
import { Box, Container, Typography, Button, Paper, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import exploreDbIcon from './image-icons/exploreDb.png';
import exploreConnIcon from './image-icons/exploreconn.png';
import exploreCurIcon from './image-icons/exploreCur.png';
import exploreDataIcon from './image-icons/exploreData.png';

const ProductsSection = () => {
  return (
    <Box
      sx={{
        width: '100vw',
        py: { xs: 6, sm: 7, md: 12 },
        background: 'linear-gradient(180deg, #1A0F2A 0%, #0F0F1A 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 4, sm: 6, md: 8, lg: 12 } }}>
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 8 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '48px' },
                lineHeight: '100%',
                letterSpacing: '0%',
                textAlign: 'center',
                mb: { xs: 2, sm: 2.5, md: 3 },
                background: 'linear-gradient(180deg, #F6F6F7 0%, #7E808F 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Explore Our Data Engineering Products
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                lineHeight: '160%',
                letterSpacing: '0px',
                textAlign: 'center',
                color: '#FFFFFFCC',
                maxWidth: '800px',
                mx: 'auto',
                mb: 2,
                px: { xs: 1, sm: 0 },
              }}
            >
              Transform how your data moves, evolves, and delivers value across your business.
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                lineHeight: '160%',
                letterSpacing: '0px',
                textAlign: 'center',
                color: '#FFFFFFCC',
                maxWidth: '800px',
                mx: 'auto',
                px: { xs: 1, sm: 0 },
              }}
            >
              Our unified platform turns fragmented workflows into a streamlined, high-impact data engine.
            </Typography>
          </motion.div>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        >
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, rgba(74, 142, 247, 0.2) 0%, rgba(58, 80, 251, 0.2) 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0px 25px 50px -12px #00000040',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, rgba(59, 130, 246, 0) 0%, #3B82F6 50%, rgba(59, 130, 246, 0) 100%)',
                zIndex: 1,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'flex-start' },
                p: { xs: 2, sm: 2.5, md: 4 },
                gap: { xs: 2, sm: 2.5, md: 6 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '22px', sm: '25px', md: '28px' },
                    lineHeight: { xs: '28px', sm: '32px', md: '36.4px' },
                    letterSpacing: '0.1px',
                    color: '#FFFFFF',
                    mb: { xs: 1, sm: 1.25, md: 2 },
                  }}
                >
                  ETL & ELT
                </Typography>
                
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    lineHeight: '100%',
                    letterSpacing: '-0.31px',
                    color: '#FFFFFFCC',
                    mb: { xs: 2, sm: 2.5, md: 4 },
                  }}
                >
                  Build transformations through a visual interface or SQL and deliver clean, accurate data to any destination at scale.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 1.25, md: 2 },
                    mb: { xs: 2, sm: 2.5, md: 4 },
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label="Visual transformation"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="SQL processing"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Multi-destination"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                </Box>

                <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '13px', md: '14px' },
                    lineHeight: '20px',
                    letterSpacing: '-0.15px',
                    background: 'linear-gradient(90deg, #00B8DB 0%, #2B7FFF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textTransform: 'none',
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      background: 'linear-gradient(90deg, #00A8CB 0%, #1B6FFF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      '& .MuiSvgIcon-root': {
                        transform: 'translateX(4px)',
                        color: '#2B7FFF',
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      transition: 'transform 0.3s ease',
                      color: '#2B7FFF',
                    },
                  }}
                >
                  Learn more
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={exploreDbIcon}
                  alt="Database Icon"
                  sx={{
                    width: { xs: 80, md: 100, lg: 120 },
                    height: 'auto',
                    filter: 'drop-shadow(0 0 20px rgba(96, 165, 250, 0.2))',
                    imageRendering: 'crisp-edges',
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
        >
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, rgba(138, 90, 249, 0.2) 0%, rgba(131, 76, 255, 0.2) 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0px 25px 50px -12px #00000040',
              overflow: 'hidden',
              position: 'relative',
              mt: { xs: 2, sm: 2.5, md: 4 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, rgba(139, 92, 246, 0) 0%, #8B5CF6 50%, rgba(139, 92, 246, 0) 100%)',
                zIndex: 1,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'flex-start' },
                p: { xs: 2, sm: 2.5, md: 4 },
                gap: { xs: 2, sm: 2.5, md: 6 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '22px', sm: '25px', md: '28px' },
                    lineHeight: { xs: '28px', sm: '32px', md: '36.4px' },
                    letterSpacing: '0.1px',
                    color: '#FFFFFF',
                    mb: { xs: 1, sm: 1.25, md: 2 },
                  }}
                >
                  Pipelines & Orchestration
                </Typography>
                
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    lineHeight: '100%',
                    letterSpacing: '-0.31px',
                    color: '#FFFFFFCC',
                    mb: { xs: 2, sm: 2.5, md: 4 },
                  }}
                >
                  Automate pipeline runs with scheduling, dependencies, and real-time monitoring for smooth, dependable data operations.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 1.25, md: 2 },
                    mb: { xs: 2, sm: 2.5, md: 4 },
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label="Automated workflows"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Real-time monitoring"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Smart scheduling"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                </Box>

                <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '13px', md: '14px' },
                    lineHeight: '20px',
                    letterSpacing: '-0.15px',
                    background: 'linear-gradient(90deg, #5B3DA0 0%, #DD56E4 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textTransform: 'none',
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      background: 'linear-gradient(90deg, #4B2D90 0%, #CD46D4 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      '& .MuiSvgIcon-root': {
                        transform: 'translateX(4px)',
                        color: '#DD56E4',
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      transition: 'transform 0.3s ease',
                      color: '#DD56E4',
                    },
                  }}
                >
                  Learn more
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={exploreConnIcon}
                  alt="Pipeline Icon"
                  sx={{
                    width: { xs: 80, md: 100, lg: 120 },
                    height: 'auto',
                    filter: 'drop-shadow(0 0 20px rgba(139, 92, 246, 0.2))',
                    imageRendering: 'crisp-edges',
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
        >
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(0, 206, 138, 0.2) 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderTop: '1px solid #FFFFFF1A',
              boxShadow: '0px 25px 50px -12px #00000040',
              overflow: 'hidden',
              position: 'relative',
              mt: { xs: 2, sm: 2.5, md: 4 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, rgba(16, 185, 129, 0) 0%, #10B981 50%, rgba(16, 185, 129, 0) 100%)',
                zIndex: 1,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'flex-start' },
                p: { xs: 2, sm: 2.5, md: 4 },
                gap: { xs: 2, sm: 2.5, md: 6 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '22px', sm: '25px', md: '28px' },
                    lineHeight: { xs: '28px', sm: '32px', md: '36.4px' },
                    letterSpacing: '0.1px',
                    color: '#FFFFFF',
                    mb: { xs: 1, sm: 1.25, md: 2 },
                  }}
                >
                  Telemetry & Log Transformation
                </Typography>
                
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    lineHeight: '100%',
                    letterSpacing: '-0.31px',
                    color: '#FFFFFFCC',
                    mb: { xs: 2, sm: 2.5, md: 4 },
                  }}
                >
                  Process, filter, and route log data at scale with masking, enrichment, and optimization.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 1.25, md: 2 },
                    mb: { xs: 2, sm: 2.5, md: 4 },
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label="Scale processing"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Data masking"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Smart routing"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Enrichment"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                </Box>

                <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '13px', md: '14px' },
                    lineHeight: '20px',
                    letterSpacing: '-0.15px',
                    background: 'linear-gradient(90deg, #0DFFB4 0%, #0D9067 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textTransform: 'none',
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      background: 'linear-gradient(90deg, #0DEFB4 0%, #0D8057 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      '& .MuiSvgIcon-root': {
                        transform: 'translateX(4px)',
                        color: '#0DFFB4',
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      transition: 'transform 0.3s ease',
                      color: '#0DFFB4',
                    },
                  }}
                >
                  Learn more
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={exploreCurIcon}
                  alt="Telemetry Icon"
                  sx={{
                    width: { xs: 80, md: 100, lg: 120 },
                    height: 'auto',
                    filter: 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.2))',
                    imageRendering: 'crisp-edges',
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
        >
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, rgba(245, 158, 11, 0.2) 100%)',
              borderRadius: '8px',
              border: '1px solid #FFFFFF1A',
              boxShadow: '0px 25px 50px -12px #00000040',
              overflow: 'hidden',
              position: 'relative',
              mt: { xs: 2, sm: 2.5, md: 4 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, rgba(245, 158, 11, 0) 0%, #F59E0B 50%, rgba(245, 158, 11, 0) 100%)',
                zIndex: 1,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'flex-start' },
                p: { xs: 2, sm: 2.5, md: 4 },
                gap: { xs: 2, sm: 2.5, md: 6 },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '22px', sm: '25px', md: '28px' },
                    lineHeight: { xs: '28px', sm: '32px', md: '36.4px' },
                    letterSpacing: '0.1px',
                    color: '#FFFFFF',
                    mb: { xs: 1, sm: 1.25, md: 2 },
                  }}
                >
                  Data Migration Engine
                </Typography>
                
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    lineHeight: '100%',
                    letterSpacing: '-0.31px',
                    color: '#FFFFFFCC',
                    mb: { xs: 2, sm: 2.5, md: 4 },
                  }}
                >
                  Move data across SaaS apps or databases seamlessly with zero-downtime migrations.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 1.25, md: 2 },
                    mb: { xs: 2, sm: 2.5, md: 4 },
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label="Zero downtime"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Cross-platform"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                  <Chip
                    label="Seamless migration"
                    sx={{
                      background: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: '8px',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '20px',
                      letterSpacing: '-0.15px',
                      color: '#CAD5E2',
                      height: { xs: '36px', md: '40px' },
                      px: { xs: 1.5, md: 2 },
                    }}
                  />
                </Box>

                <Button
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '13px', md: '14px' },
                    lineHeight: '20px',
                    letterSpacing: '-0.15px',
                    background: 'linear-gradient(90deg, #F59E0B 0%, #FEC056 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textTransform: 'none',
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      background: 'linear-gradient(90deg, #E58E0B 0%, #EEB046 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      '& .MuiSvgIcon-root': {
                        transform: 'translateX(4px)',
                        color: '#FEC056',
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      transition: 'transform 0.3s ease',
                      color: '#FEC056',
                    },
                  }}
                >
                  Learn more
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={exploreDataIcon}
                  alt="Migration Icon"
                  sx={{
                    width: { xs: 80, md: 100, lg: 120 },
                    height: 'auto',
                    filter: 'drop-shadow(0 0 20px rgba(245, 158, 11, 0.2))',
                    imageRendering: 'crisp-edges',
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ProductsSection;

